import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Users, Globe, Lock, AlertCircle, CheckCircle2 } from "lucide-react";
import { analytics } from "@/lib/analytics";

export default function JoinLeague() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [joining, setJoining] = useState(false);

  const { data: league, isLoading, error } = useQuery({
    queryKey: ["league-by-invite", inviteCode],
    enabled: !!inviteCode && !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("id, name, emoji, member_count, max_members, is_public")
        .eq("invite_code", inviteCode!.toUpperCase())
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: membership } = useQuery({
    queryKey: ["membership-check", league?.id, user?.id],
    enabled: !!league?.id && !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("league_members")
        .select("id")
        .eq("league_id", league!.id)
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  const isMember = !!membership;
  const isFull = league && league.max_members && (league.member_count ?? 0) >= league.max_members;

  const handleJoin = async () => {
    if (!league || !user) return;
    setJoining(true);
    const { error } = await supabase
      .from("league_members")
      .insert({ league_id: league.id, user_id: user.id, role: "member" });
    setJoining(false);
    if (error) return;
    analytics.track("league_joined", { method: "invite_link" });
    queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
    navigate(`/leagues/${league.id}`, { replace: true });
  };

  // Redirect unauthenticated users to sign-in
  if (!authLoading && !user) {
    const redirectPath = `/join/${inviteCode}`;
    window.location.href = `/sign-in?redirect=${encodeURIComponent(redirectPath)}`;
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-muted-foreground">Redirecting to sign in…</p>
      </div>
    );
  }

  if (authLoading || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-sm">
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <CardTitle className="text-lg">Invalid Invite Link</CardTitle>
            <CardDescription>This invite link is invalid or expired.</CardDescription>
            <Button variant="outline" className="mt-2" onClick={() => navigate("/leagues")}>
              Go to Leagues
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <span className="text-5xl">{league.emoji || "🏆"}</span>
          <CardTitle className="text-xl mt-2">{league.name}</CardTitle>
          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground mt-1">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" /> {league.member_count ?? 0} members
            </span>
            <span className="flex items-center gap-1">
              {league.is_public ? <><Globe className="h-4 w-4" /> Public</> : <><Lock className="h-4 w-4" /> Private</>}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {isMember ? (
            <div className="space-y-3 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                You're already in this league
              </div>
              <Button className="w-full font-semibold" onClick={() => navigate(`/leagues/${league.id}`)}>
                Go to League
              </Button>
            </div>
          ) : isFull ? (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">This league is full.</p>
              <Button variant="outline" className="mt-3 w-full" onClick={() => navigate("/leagues")}>
                Go to Leagues
              </Button>
            </div>
          ) : (
            <Button className="w-full font-semibold" disabled={joining} onClick={handleJoin}>
              {joining ? "Joining…" : "Join League"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
